/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 * Drag-and-Drop Manager
 */
define([
	'jquery',
	'underscore',
	'../../lib/@waca/core-client/js/core-client/utils/dom-utils',
	'../../lib/@waca/core-client/js/core-client/utils/BrowserUtils',
	'../DialogBlocker'
], function($, _, utils, BrowserUtils, DialogBlocker) {

	function disableSelect(event) {
		event.preventDefault();
	}

	let ID_COUNT = 0;
	return class DnDManager {
		constructor() {
			this.dropTargets = [];
			this.candidateDropTargets = [];
			this.currentDropTarget = {};
			this._dropHandlers = [];
			this.dragObject = null;
			this.$avatarContainer = $('<div class="avatarContainer"/>');

			if (BrowserUtils.isIPad()) { //Add a touchmove handler to the document on iPad to kill default page scroll when dragging. This needs to be present before startDrag() or it does not function.
				document.addEventListener('touchmove', this.preventPageScrollDrag.bind(this), { passive:false });
			}

			DialogBlocker.dialogBlockerClasses.forEach(className => {
				// Add the dialog blocker as a default drop zone to swallow and ignore any drop that happens on the backdrop
				// We will also count the toolkit
				this.addDropTarget($('body')[0], `.${className}`, {
					accepts: function() { return true; }
				});
			});
		}

		preventPageScrollDrag(evt) {
			if (this.isDragStartCalled) {
				evt.preventDefault();
			}
		}

		// Utility method that subscribes to the specified jQuery event
		// and returns removable object, that can be inserted into the 'owned' collections
		// of subscriptions
		//
		on($el, types, selector, data, fn) {
			$el.on(types, selector, data, fn);
			return {
				remove: function() {
					$el.off(types, selector, fn);
				}
			};
		}

		/**
		 * Register a drop Zone with the drag and drop manage
		 *
		 *
		 * @param el - The drop zone element.
		 * @param selector - Selector to identify the drop zone sub regions. If not specified, there will only be one region which is the drop zone element.
		 * @param callbacks
		 *		callbacks.accepts(dragObject, node)  - Called to check if dropzone (the node element) accepts the payload.
		 *		callbacks.onDragStart() - Called when a drag starts
		 *		callbacks.onDragEnd() - Called when a drag ends
		 *		callbacks.onDragEnter(dragObject, dropNode) - Called when we enter a drop zone
		 *		callbacks.onDragMove(dragObject, dropNode) - Called when we move inside a drop zone
		 *		callbacks.onDragLeave(dragObject, dropNode) - Called when we leave a drop zone
		 *		callbacks.onDrop(dragObject, dropNode) - Called when a drop occurs.
		 * @returns {___anonymous1282_1365}
		 */
		addDropTarget(el, selector, callbacks) {
			return this._addDropTarget(el, selector, callbacks);
		}

		/**
		 * Get a drop target from the registered drop targets
		 * @param {Element} el - Element of drop target to get from drop targets.
		 */
		getDropTarget(el) {
			return _.find(this.dropTargets, t => t.el === el);
		}

		/**
		 * Register a drop Zone with no callbacks for Dnd Manager
		 *
		 * @param {Object} options - DropTarget payload
		 * @param options.el - The drop zone element.
		 * @param options.type - DropTarget type
		 * @param options.info - Function called to evaluate drop payload co-oridnates and drop zone dom node
		 */
		registerDropTarget({ el, type, info }) {
			this._addDropTarget(el, undefined, undefined, info, type);
			return {
				el: el,
				remove: () => this.deregisterDropTarget(el)
			};
		}

		_addDropTarget(el, selector, callbacks, info, type) {
			if (typeof selector !== 'string') {
				callbacks = selector || {};
				selector = null;
			}
			const defaultInfo = () => {};
			callbacks = callbacks ? callbacks : {};

			this.removeDropTarget(el, selector);

			this.dropTargets.push({
				el: el,
				selector: selector,
				callbacks: callbacks,
				info: info || callbacks.info || defaultInfo,
				type: type || callbacks.type || 'legacyDropTarget'
			});

			return {
				remove: function() {
					this.removeDropTarget(el);
				}.bind(this)
			};
		}

		/**
		 * Register a drop handler for the drag and drop manager
		 *
		 * TODO: Add "options.filter" to input params of dropHandler
		 * @param {Object} options - Input object for registering a drop handler
		 * @param {Function} options.accepts - Called to check if dropzone (the node element) accepts the payload.
		 * @param {Function} options.onDrop - Called when a drop occurs in the drop target.
		 */
		registerDropHandler({ accepts, onDrop }) {
			const handler = {
				id: Date.now() + '_' + ID_COUNT++,
				accepts: accepts,
				onDrop: onDrop
			};
			this._dropHandlers.push(handler);
			return {
				id: handler.id,
				deregister: () => {
					this.deregisterDropHandler(handler.id);
				}
			};
		}

		/**
		 * Get a drop handler from the registered drop handlers
		 * @param {String} id - id of drop handler to get from drop handlers.
		 */
		getDropHandler(id) {
			return this._dropHandlers.find(t => t.id === id);
		}

		/**
		 * Remove a registered drop handler
		 * @param id - This is the id of the drop handler to be deregistered or removed
		 */
		deregisterDropHandler (id) {
			const handler = this.getDropHandler(id);
			if (handler) {
				this._dropHandlers.splice(_.indexOf(this._dropHandlers, handler), 1);
			}
		}

		/**
		 * Remove a registered drop target
		 * @param el
		 * @param selector(Optional), this will scoped down the target with selector when el is not unique
		 */
		removeDropTarget(el, selector) {
			const target = _.find(this.dropTargets, (t) => {
				return t.el === el && (selector ? t.selector === selector : true);
			});
			if (target) {
				this.dropTargets.splice(_.indexOf(this.dropTargets, target), 1);
			}
		}

		/**
		 * Deregister a drop target
		 * @param el
		 */
		deregisterDropTarget(el) {
			this.removeDropTarget(el);
		}

		_isScrollDropSupported() {
			return (this.currentDropTarget.target && this.currentDropTarget.target.callbacks.isScrollDropSupported ? true : false);
		}

		/**
		 *
		 * Check if the intention is scrolling and return the proper scrolling enabled drop zone.
		 * If we are dragging outside of the view port, the intention is to auto scroll. In this case we do the following:
		 * 		- if we have a drop zone, find the closest scrollable drop zone in the ancestors.
		 *      - if there is no drop zone, select the last scrollable drop zone that was used.
		 *
		 * If we are dragging within the view and we have no drop zone, find the closest scrolling drop zone using the last drop target that was used.
		 * @param target
		 * @param isScrollDropSupported
		 * @param pos - current mouse position
		 *
		 * @returns the target or empty object.
		 */
		_validateDropTarget(target, isScrollDropSupported, pos) {
			var foundTarget = target;
			if (isScrollDropSupported){
				if ((pos.x + 1) >= $(window).innerWidth() || (pos.y + 1) >= $(window).height() || pos.x <= 1 || pos.y <= 1){
					// dragging outside of the viewport.. most likely the intent is to auto scroll a drop zone
					if (foundTarget) {
						foundTarget = this._getClosestTargetWithScrollSupport(foundTarget.node);
					} else {
						foundTarget = this.lastActiveScrollableTarget;
					}
				}

				if(!foundTarget && this.currentDropTarget){
					foundTarget = this._getClosestTargetWithScrollSupport(this.currentDropTarget.node);
				}
			}

			return (foundTarget || {});
		}

		/**
		 * Scan the list containing the ancestors + self and return the first drop zone that suppports scrolling.
		 *
		 * @param dropTargetNode - the node of the drop target where to start the search
		 *
		 */
		_getClosestTargetWithScrollSupport(dropTargetNode) {
			var parentsAndSelf = $(dropTargetNode).parents();
			parentsAndSelf.splice(0,0, dropTargetNode);
			var target = null;
			for (var i = 0; i< parentsAndSelf.length; i++) {
				target = this._getDropTargetWithScrollSupport(parentsAndSelf[i]);
				if(target) {
					this.lastActiveScrollableTarget = target;
					break;
				}
			}

			return target;

		}

		/**
		 * Find the drop zone that support scrolling where the node match the given node parameter
		 *
		 * @param targetNode - node to match with the drop zone node.
		 *
		 */
		_getDropTargetWithScrollSupport(targetNode) {
			var foundTarget = null;

			var dropTarget = _.find(this.dropTargets, function(target) {
				var $elements = $(target.el);
				if (target.selector) {
					$elements = $elements.find(target.selector);
				}

				return _.find($elements, function(node) {
					return node === targetNode &&
					target.callbacks.isScrollDropSupported &&
					(!target.callbacks.accepts || target.callbacks.accepts(this.dragObject));
				}.bind(this));
			}.bind(this));

			if (dropTarget){
				foundTarget = {
					target: dropTarget,
					node: targetNode
				};
			}
			return foundTarget;
		}

		/**
		 * Helper method that gets an array of possible drop targets at a point.
		 * It includes all overlapping drop targets and the array is sorted in the overlapping order from the deepest to the topmost
		 * @param [ev] -- mouse event passed to get mouse position and used by info method
		 * @param [pos] -- mouse position co-ordinates
		 * @param [candidateDropTargets] -- candidateDropTargets registered
		 * @returns {Array} - Sorted array of possible drop targets
		 */
		_getTargetsAtPoint(pos, candidateDropTargets) {
			const dropTargets = [];
			const candidateElements = [];
			const finalDropTargets = [];
			const allElementsFromPoint = this._getElementsFromPoint(pos);

			for (let j = 0; j < candidateDropTargets.length; j++) {
				const elements = candidateDropTargets[j].$elements;
				for (let i = 0; i < elements.length; i++) {
					candidateElements.push({
						dropTarget: candidateDropTargets[j],
						el: elements[i],
						elIndex: i
					});
				}
			}

			for (let j = 0; j < candidateElements.length; j++) {
				const target = candidateElements[j].dropTarget;
				const targetElement = candidateElements[j].el;
				const targetElementIndex = candidateElements[j].elIndex;
				const rank = Array.prototype.indexOf.call(allElementsFromPoint, targetElement);
				if (rank >= 0) {
					target.candidateNode = targetElement;
					target.candidateNodeIndex = targetElementIndex;
					if (target.callbacks && target.callbacks.receiveEventsWhenNotAccepting) {
						finalDropTargets.push(target);
					} else {
						dropTargets.push({
							priorityRank: this._getPriority(target) * 100,
							rank,
							target
						});
					}
				}
			}

			// Sorting based on the order from elementsFromPoint and the Priority from the dropTarget callback
			dropTargets.sort((dropTarget1, dropTarget2) => {
				return dropTarget2.priorityRank + dropTarget1.rank - dropTarget1.priorityRank - dropTarget2.rank;
			});
			if (dropTargets.length) {
				for (let i = dropTargets.length - 1; i >= 0; i--) {
					finalDropTargets.push(dropTargets[i].target);
				}
			}
			return finalDropTargets;
		}

		/**
		 * Helper method that gets the current drop target from a position
		 * @param pos
		 * @param [options] -- options passed by the called when the startDrag method is called
		 * @param [ev] -- mouse event passed to be used by info method
		 * @returns {Object} - drop target
		 */
		getDropTargetFromPos(pos, options, ev) {
			const isScrollDropSupported = options && !options.disableScrollableDropZoneSupport;

			let foundTarget = null;
			let foundTargetArea = 0;
			let foundTargetPriority = 0;

			let nonActiveDropZones = [];

			ev = ev ? ev : { pageX: pos.x, pageY: pos.y };

			const findTarget = function (pos, ignoreDropZone) {

				let candidateDropTargetsAtPoint = this._getTargetsAtPoint(pos, this.candidateDropTargets);
				let candidateNode = candidateDropTargetsAtPoint.map((target) => target && target.candidateNode);
				const nodeChanged = !this._prevCandidateNode || this._prevCandidateNode.length !== candidateNode.length ||
					candidateNode.some((el, idx) => el !== this._prevCandidateNode[idx] );
				if (nodeChanged) {
					this._prevFoundTarget = null;
				}
				let possibleNonActiveDropZones = [];
				_.each(candidateDropTargetsAtPoint.slice(), function(target) {
					// TODO: A drop target is part of the avatar... e.g. moving a view that has a drop target.
					// Hack.. in the case of a move, .avatarContainerDragging is created by move.js, which does not seem right
					// we should have a better way to disable the drop zone if it is being moved
					if ($('.avatarContainerDragging').find(target.$elements).length > 0){
						return false;
					}
					const node = target.candidateNode;
					const index = target.candidateNodeIndex;
					foundTargetArea = target.clientRects[index].width * target.clientRects[index].height;
					foundTargetPriority = this._getPriority(target);
					if (target.callbacks.acceptOnTopMost) {
						const options = target.callbacks.acceptOnTopMost() || { excludedClasses:[] };
						if(!this._isOnTopMost(pos, node, options.excludedClasses)) {
							return;
						}
					}
					const ignoreMatch = ignoreDropZone && $(target.el).hasClass('dropIcon');
					if ((target.callbacks.accepts && target.callbacks.accepts(this.dragObject, node)) && !ignoreMatch){
						if (nodeChanged) {
							foundTarget = {
								target: target,
								node: node
							};
						}
					} else if (target.callbacks.receiveEventsWhenNotAccepting){
						possibleNonActiveDropZones.push({
							target: target,
							node: node,
							index: index
						});
					}
					let handlerFound = false;
					if (nodeChanged) {
						for (let i = 0; i < this._dropHandlers.length; i++) {
							const isAccepted = this._dropHandlers[i].accepts({
								type: this.dragObject.type,
								data: this.dragObject.data
							}, {
								type: target.type,
								info: target.info(target.el, ev) || {}
							});
							if (isAccepted && !handlerFound) {
								foundTarget = {
									target: target,
									node: node,
									handler: this._dropHandlers[i]
								};
								handlerFound = true;
							}
						}
					}
				}.bind(this));
				this._prevCandidateNode = candidateNode;
				this._prevFoundTarget = foundTarget || this._prevFoundTarget;
				foundTarget = foundTarget || this._prevFoundTarget;

				if (foundTarget) {
					possibleNonActiveDropZones.forEach((nonActiveTarget) => {
						let info = this.getTargetMatchInformation(pos, nonActiveTarget.node, nonActiveTarget.target.clientRects[nonActiveTarget.index], nonActiveTarget.target, foundTarget, foundTargetArea, foundTargetPriority);
						if (info.isMatch) {
							nonActiveDropZones.push(nonActiveTarget);
						}
					});
				} else {
					nonActiveDropZones = possibleNonActiveDropZones;
				}
			}.bind(this);

			findTarget(pos);

			if(!foundTarget) {
				// With the drag enabled only on the perimeter and top handle of data widgets, the cursor is
				// often slightly out of bounds when dropping near the boundary. This block gives the
				// the center of the widget a chance at matching instead of the cursor position.
				const dragNodeCenter = this.getDragObjectCenterCoordinates();
				if(dragNodeCenter) {
					findTarget(dragNodeCenter, true);
				}
			}

			foundTarget = this._validateDropTarget(foundTarget, isScrollDropSupported, pos);

			if (foundTarget){
				foundTarget.nonActiveDropZones = nonActiveDropZones;
			}

			return foundTarget || {};
		}

		/**
		 * Return true if val is between lowerBound and (lowerBound + rangeSize).
		 */
		_isInRange(val, lowerBound, rangeSize) {
			return (val >= lowerBound && val < (lowerBound + rangeSize));
		}

		/**
		 * return the priority set for target or 0.
		 */
		_getPriority(target) {
			var priority = target.callbacks.priority;
			if (typeof priority === 'function') {
				priority = priority();
			}
			return (priority || 0);
		}

		/**
		 * Helper function used by getDropTargetFromPos to decide if a drop target is found or not using the current mouse position
		 */
		getTargetMatchInformation(pos, node, clientRect, target, foundTarget, foundTargetArea, foundTargetPriority) {
			var matchInformation = {};

			var xInRange = this._isInRange(pos.x, clientRect.left, clientRect.width);
			var yInRange = this._isInRange(pos.y, clientRect.top, clientRect.height);

			if( xInRange && yInRange ) {
				matchInformation = {
					area: clientRect.width * clientRect.height,
					priority: this._getPriority(target)
				};
				matchInformation.isMatch = !foundTarget || matchInformation.priority > foundTargetPriority || (foundTargetArea > matchInformation.area && matchInformation.priority >= foundTargetPriority) ||
					target.callbacks.isMatch && target.callbacks.isMatch(node);
			}
			return matchInformation;
		}

		getDragObjectCenterCoordinates() {
			if(this.dragObject && this.dragObject.data && this.dragObject.data.nodeInfoList) {
				var xSum = 0;
				var ySum = 0;
				_.each(this.dragObject.data.nodeInfoList, function(nodeInfoListItem) {
					var nodeRect = nodeInfoListItem.node.getBoundingClientRect();
					xSum += nodeRect.left + (nodeRect.width / 2);
					ySum += nodeRect.top + (nodeRect.height / 2);
				});
				return {
					x: xSum / this.dragObject.data.nodeInfoList.length,
					y: ySum / this.dragObject.data.nodeInfoList.length
				};
			}
			return null;
		}

		/**
		 * Helper method that gets the current drop target from a node
		 * @param pos
		 * @returns {Object} - drop target
		 */
		getDropTargetFromNode(node) {

			var target = null;
			for (var i = 0; i < this.dropTargets.length; i++){
				if( node === this.dropTargets[i].el ) {
					target =  this.dropTargets[i];
					break;
				}
			}

			return target;
		}

		/**
		 * Helper method that gets the current candidate drop target which includes the cached clentRect from a node
		 * @param node
		 * @returns {Object} - drop target
		 */
		getCandidateDropTargetFromNode(node) {
			return this.candidateDropTargets.find((candidateDropTarget) => candidateDropTarget.el === node);
		}

		/**
		 * Called to start dragging
		 * @param options
		 *		options.event ev - the event used to start the drag. e.g. mousedown, touchstart
		 *		options.type - The type of the data being dropped.
		 *		options.data - The data being dropped.
		 *		options.avatar - the avatar node associated with the drag and drop operation
		 *		options.avatarXOffset - the number of pixel added to the avatar left position
		 *		options.avatarYOffset - the number of pixel added to the avatar top position
		 *		options.restrictToXAxis - restrict moving on the x axis
		 *		options.restrictToYAxis - restrict moving on the y axis
		 *
		 *		options.callerCallbacks
		 *			callerCallback.onDragStart(event, {dragObject}) - Called when the drag is started.
		 *			callerCallback.onMove(event, {dragObject, dropNode}) - Called on every move event.
		 *			callerCallback.onDragDone(event, {dragObject, dropNode, isDropped}) - Called when the drag is complete, whether we droppped or not.
		 *
		 *		options.moveXThreshold - the number of pixel to move on the X axis before we allow the drag to start. A good example to use this is
		 *			when want to enable a drag horizontally and still allow vertical scrolling/panning. In this case, we would set the moveXThreshold to something like 30.
		 *			This will allow vertical scroll as long as we don't drag horizontally more that 30 pixels.
		 *
		 *		options.moveYThreshold - the number of pixel to move on the Y axis before we allow the drag to start.
		 *
		 *		options.dragLockToAxis - once one of the move thresholds is met, restrict drag to that axis.
		 *
		 * @param moveThreshold - distance in pixel that will activate the move and the avatar
		 */
		startDrag(options) {
			if (this.dragObject) {
				// cannot start another drag operation until the previous one is finished
				return;
			}
			$('body').addClass('preventSelection');
			window.addEventListener('selectstart', disableSelect );
			this.isDragStartCalled = false;

			this.targetMap = {};

			this._prevFoundTarget = null;
			this._prevCandidateNode = null;
			var isTouch = utils.isTouchEvent(options.event);
			this.dragObject = {
				//eg. The type of dragObject. 'widget', 'pin'
				type: options.type,
				//the type of widget (only for widget): eg. 'Shape'
				widgetType: options.widgetType,
				widgetSpec: options.widgetSpec,
				data: options.data,
				avatar:options.avatar,
				isTouch: isTouch || options.event.gesture !== undefined
			};

			this.dragObject.startPosition = this._fixPos(utils.getEventPos(options.event));

			// In the case of using HammerJS v1 'hold' event, we want the avatar to show up
			// immediately (when the hold event is fired). To do that we need to set the dragObect.position
			// object before calling the DnDManager.setAvatar() method.
			// Additionally the position will be the same as the startPosition as this is the start of the drag
			if(options.showAvatarImmediately) {
				this.dragObject.position = this.dragObject.startPosition;
			}

			this.setAvatar(options.avatar, options);

			this.callerCallbacks = options.callerCallbacks ? options.callerCallbacks : {};

			var $target = $(window);
			if (isTouch) {
				$target = $(utils.getEventTarget(options.event));
			}
			this.attachedMoveHandler = this.on($target, 'mousemove touchmove', _.throttle(this.moveHandler.bind(this, options), 10, { trailing: false }));
			this.attachedUpHandler = this.on($target, 'mouseup touchend touchcancel', this.upHandler.bind(this));
			if(options.event.type === 'mousedown') {
				this.attachedScrollHandler = this.on($(options.event.target),'scroll', this.scrollHandler.bind(this));
			}

			if(options.currentDropZoneNode){
				var target = this.getDropTargetFromNode(options.currentDropZoneNode);
				if (target){
					this.currentDropTarget = {
						target: target,
						node: options.currentDropZoneNode
					};
				}
			}

		}

		scrollHandler(/* event */) {
			// we are scrolling, do not track movement
			// cancel drag
			// IE does not fire mouseUp on scrollbar so drag move continues after releasing the mouse when it should not
			this.resetDragging();
		}

		_setAvatarPosition(/*options*/) {
			if (this.avatar && this.dragObject.position) {
				if (!this.avatar.parentNode){
					this.$avatarContainer.append(this.avatar);
					$('body').append(this.$avatarContainer);
				}
				$(this.avatar).css({
					left: this.dragObject.position.x + this.avatarXOffset + 'px',
					top: this.dragObject.position.y  + this.avatarYOffset + 'px'
				});
			}
		}

		setAvatar(avatar, options) {
			this.avatar = avatar;
			this.avatarXOffset = options && options.avatarXOffset ? options.avatarXOffset: 1;
			this.avatarYOffset = options && options.avatarYOffset ? options.avatarYOffset: 1;
			this._setAvatarPosition(options);
		}

		/**
		 * Move event main handler
		 * @param options - drag options
		 * @param ev
		 */
		moveHandler(options, ev) {

			//Always prevent the default behavior on drag move to prevent chrome auto scrolling
			ev.preventDefault();

			this.dragObject.position = this._fixPos(utils.getEventPos(ev));

			// apply restrictions before checking the threshold so the user can't trigger the threshold in the non allowed direction
			if(options.restrictToXAxis) {
				this.dragObject.position.y = this.dragObject.startPosition.y;
			}

			if(options.restrictToYAxis) {
				this.dragObject.position.x = this.dragObject.startPosition.x;
			}

			if (!this.isDragStartCalled) {
				if( this._isThresholdNotMet(options)) {
					return;
				}
				this._lockDragAxis(options);
			}

			this._setAvatarPosition(options);

			this._callStartDrag(ev);

			var dropTarget = this.getDropTargetFromPos(this.dragObject.position, options, ev);

			this._addDropTargetActiveClass(this.currentDropTarget, dropTarget);

			this._callMove(dropTarget, ev);

			this._processCallbacks(dropTarget, ev);

			// Don't stop the propagation. This will interfere with hammer gestures like tap and hold.
			// If there is an active tap/hold handlers, hammer needs to know when there is a move.
		}

		_lockDragAxis(options){
			if(options.dragLockToAxis){
				var xDelta = Math.abs(this.dragObject.position.x - this.dragObject.startPosition.x);
				var yDelta = Math.abs(this.dragObject.position.y - this.dragObject.startPosition.y);
				if( xDelta > yDelta ) {
					options.restrictToXAxis = true;
					this.dragObject.position.y = this.dragObject.startPosition.y;
				}else{
					options.restrictToYAxis = true;
					this.dragObject.position.x = this.dragObject.startPosition.x;
				}
			}
		}

		_processCallbacks(dropTarget, event){

			var currentNonActiveDropZones  = this.currentDropTarget.nonActiveDropZones;

			// Process the callback for the active drop zone that accepted the drop
			// if the dragtarget was indicated to not be the top most, instead of repeatedly leave and enter, we should proceed move
			if (dropTarget.target !== this.currentDropTarget.target || dropTarget.node !== this.currentDropTarget.node) {
				this._dropTargetCallback(this.currentDropTarget, 'onDragLeave', event);
				this.currentDropTarget = dropTarget;
				this._dropTargetCallback(this.currentDropTarget, 'onDragEnter', event);
			} else{
				this._dropTargetCallback(this.currentDropTarget, 'onDragMove', event);
			}

			// Process callback for drop zone that didn't accept but chose to receive the event anyway
			this._processCallbacksForNonActiveDropzones(dropTarget.nonActiveDropZones, currentNonActiveDropZones, event);

		}

		_processCallbacksForNonActiveDropzones(newDropZones, oldDropZones, event){

			_.each(newDropZones, function(dropZone){
				if (this._isDropZoneInArray(dropZone, oldDropZones)){
					this._dropTargetCallback(dropZone, 'onDragMove', event);
				}else {
					this._dropTargetCallback(dropZone, 'onDragEnter', event);
				}
			}.bind(this));

			if (oldDropZones){
				_.each(oldDropZones, function(dropZone){
					if (!this._isDropZoneInArray(dropZone, newDropZones)){
						this._dropTargetCallback(dropZone, 'onDragLeave', event);
					}
				}.bind(this));
			}

			this.currentDropTarget.nonActiveDropZones = newDropZones;

		}

		_dropTargetCallback(dropTarget, callbackName, event){
			if (callbackName === 'onDrop' && dropTarget.handler) {
				dropTarget.handler.onDrop({
					type: this.dragObject.type,
					data: this.dragObject.data
				}, {
					type: dropTarget.target.type,
					info: dropTarget.target.info(dropTarget.node, event)
				});
				return true;
			} else if (dropTarget.target && dropTarget.target.callbacks[callbackName]) {
				dropTarget.target.callbacks[callbackName](this.dragObject, dropTarget.node, event);
				return true;
			} else {
				return false;
			}
		}
		_isDropZoneInArray(dropZone, dropZoneArray){
			var found = false;
			if (dropZoneArray){
				for (var i = 0 ; i< dropZoneArray.length; i++){
					if (dropZone.target === dropZoneArray[i].target){
						found = true;
						break;
					}
				}
			}

			return found;

		}

		_isThresholdNotMet(options){
			var hasThreshold = options.moveXThreshold || options.moveYThreshold;
			var isXThresholdNotMet = !options.moveXThreshold || options.moveXThreshold > Math.abs(this.dragObject.position.x - this.dragObject.startPosition.x);
			var isYThresholdNotMet = !options.moveYThreshold || options.moveYThreshold > Math.abs(this.dragObject.position.y - this.dragObject.startPosition.y);

			return hasThreshold && isXThresholdNotMet && isYThresholdNotMet;
		}

		_callMove(dropTarget, ev){
			if (this.callerCallbacks.onMove) {
				this.callerCallbacks.onMove(ev, {
					dragObject: this.dragObject,
					dropTargetNode: dropTarget.node
				});
			}
		}

		_isCandidateDropTarget(target) {
			var clientRects = [];
			var $elements = $(target.el);
			if (target.selector) {
				$elements = $elements.find(target.selector);
			}
			$elements = _.filter($elements, function(el) {
				var $el = $(el);
				var isVisible = $el.is(':visible') && $el.css('opacity') !== '0';
				if(isVisible) {
					var clientRect = el.getBoundingClientRect();
					if(clientRect.width > 1 && clientRect.height > 1) {
						clientRects.push({
							top: clientRect.top,
							left: clientRect.left,
							width: clientRect.width,
							height: clientRect.height
						});
						return true;
					}
				}
				return false;
			});
			if($elements.length) {
				target.clientRects = clientRects;
				target.$elements = $elements;
				return true;
			}
			return false;
		}

		reassessDropTarget(target) {
			var candidate = _.findWhere(this.dropTargets, target);
			if(candidate) {
				this.candidateDropTargets = _.without(this.candidateDropTargets, candidate);
				if(this._isCandidateDropTarget(candidate)) {
					this.candidateDropTargets.push(candidate);
				}
			}
		}

		/**
		 * Updates the children cached boundingRects of an el
		 * without repainting in the event that it is scrolled while
		 * a drag and drop operation is in progress.
		 */
		scrollChildrenCandidateDropTargets(el, deltaScrollTop, deltaScrollLeft) {
			this.candidateDropTargets.forEach((dropTarget) => {
				if($.contains(el, dropTarget.el)) {
					dropTarget.clientRects.forEach((clientRect) => {
						clientRect.top += deltaScrollTop;
						clientRect.left += deltaScrollLeft;
					});
				}
			});
		}

		/**
		 *  Add a css class to the active drop zone so that it can be styled if we want to
		*/
		_addDropTargetActiveClass(oldDropTarget, newDropTarget){
			const previousNode = oldDropTarget && oldDropTarget.node ;
			if (newDropTarget && newDropTarget.node !== previousNode){
				$(previousNode).removeClass('activeDropzone');
				$(newDropTarget.node).addClass('activeDropzone');
			}
		}

		/**
		 *  Remove the css class that tags the node as active drop zone
		*/
		_removeDropTargetActiveClass(){
			if (this.currentDropTarget && this.currentDropTarget.node ){
				$(this.currentDropTarget.node).removeClass('activeDropzone');
			}
		}

		_callStartDrag(ev) {
			if(!this.isDragStartCalled) {

				this._addDropTargetActiveClass(null, this.currentDropTarget);

				this.candidateDropTargets = _.filter(this.dropTargets, this._isCandidateDropTarget.bind(this));
				if (this.callerCallbacks.onDragStart) {
					this.callerCallbacks.onDragStart(ev, { dragObject: this.dragObject });
					_.each(this.candidateDropTargets, function(target) {
						if(target.callbacks.onDragStart){
							target.callbacks.onDragStart(this.dragObject);
						}
					}.bind(this));
				}
				$('body').addClass('dragging');
			}
			this.isDragStartCalled = true;
		}

		/**
		 *  mouseup/touchend main handler
		 *
		 * @param ev
		 */
		upHandler(ev) {
			var isDropped = false;
			if (this.isDragStartCalled) {
				var dropTarget = this.currentDropTarget;
				// Don't just accept the currentDropTarget, because the mouse/touch could have wandered into some place that isn't
				// a drop target and the value of currentDropTarget is a remnant to support auto-scrolling
				var foundObject = this.getDropTargetFromPos(this._fixPos(utils.getEventPos(ev)), undefined, ev);

				if (foundObject.node === dropTarget.node && this._dropTargetCallback(dropTarget, 'onDrop', ev) ) {
					isDropped = true;
				}

				if (this.callerCallbacks.onDragDone) {
					isDropped = this.callerCallbacks.onDragDone(ev, {
						dragObject: this.dragObject,
						dropTargetNode: isDropped ? dropTarget.node : null,
						isDropped: isDropped
					}) || isDropped;
				}

				if (dropTarget.nonActiveDropZones){
					_.each(dropTarget.nonActiveDropZones, function(dropZone){
						this._dropTargetCallback(dropZone, 'onDrop');
					}.bind(this));
				}

				_.each(this.candidateDropTargets, function(target) {
					if(target.callbacks.onDragEnd){
						target.callbacks.onDragEnd(this.dragObject);
					}
				}.bind(this));
			}

			this.resetDragging(isDropped);

			// Don't stop the propagation. This will interfere with hammer gestures like tap and hold.
			// If there is an active tap/hold handlers, hammer needs to know when the touch ends.
		}

		destroy(){
			// Things to do before deleting a DnDManager.
			if (this.attachedMoveHandler){
				this.attachedMoveHandler.remove();
			}
		}

		resetDragging(isDropped){
			// Release the capture that we set in the mouse down.
			// This is used to allow IE to keep firing the mouse event when the mouse leaves the window or iframe
			if(document.releaseCapture) {
				document.releaseCapture();
			}

			if (this.attachedMoveHandler){
				this.attachedMoveHandler.remove();
			}
			if (this.attachedUpHandler){
				this.attachedUpHandler.remove();
			}

			if (this.attachedScrollHandler){
				this.attachedScrollHandler.remove();
			}

			$('body').removeClass('preventSelection');
			$('body').removeClass('dragging');
			window.removeEventListener('selectstart', disableSelect );

			this.candidateDropTargets = [];

			this._removeDropTargetActiveClass();

			this.currentDropTarget = {};
			this.targetMap = null;
			this.lastActiveScrollableTarget = null;
			if (isDropped === false && this.isDragStartCalled === true) {
				const avatar = this.avatar,
					$avatarContainer = this.$avatarContainer;
				$(avatar).animate({
					left: this.dragObject.startPosition.x + this.avatarXOffset,
					top: this.dragObject.startPosition.y + this.avatarYOffset
				}, 500, function() {
					if(avatar){
						$(avatar).remove();
						$avatarContainer.remove();
					}
				}.bind(this));
			} else {
				if(this.avatar){
					$(this.avatar).remove();
					this.$avatarContainer.remove();
				}
			}
			this.isDragStartCalled = false;
			this.dragObject = null;
		}

		_fixPos(eventPos) {
			return { x: eventPos.pageX,
				y: eventPos.pageY };
		}

		_getElementsFromPoint(pos) {
			if (document.elementsFromPoint) {
				return document.elementsFromPoint(pos.x, pos.y);
			} else if (document.msElementsFromPoint) {
				return document.msElementsFromPoint(pos.x, pos.y);
			} else {
				return [document.elementFromPoint(pos.x, pos.y)];
			}
		}

		_isOnTopMost(pos, node, excludedClasses) {
			const elementsFromPoint = this._getElementsFromPoint(pos);
			const topMostNode = _.find(elementsFromPoint, function(element) {
				//Ignore any ui-toolkit DnD elements. Otherwise, Sudden mouse movement will cause the drop target to flash
				if (element.className && _.isFunction(element.className.startsWith)) {
					return !element.className.startsWith('ba-common');
				} else {
					return true;
				}
			});
			const found = _.find(excludedClasses, function(className) {
				return $(topMostNode).hasClass(className);
			});
			if (!found) {
				return node.contains(topMostNode);
			}
			return true;
		}

		reassessContainer(el) {
			this.dropTargets.filter((t) =>{
				if (t.el.jquery) {
					return false;
				}
				const $el = $(t.el);
				const isVisible = $el.is(':visible') && $el.css('opacity') !== '0';
				return isVisible && el.contains(t.el);
			}).forEach((target) =>this.reassessDropTarget(target));
		}
	};
});
